import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, AlertTriangle, Check, FileSignature, Download, X } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Mono, Field, money, inputCls, DL_CLR } from "./ContractAdmin";

function PromptModal({ title, label, initial, confirmText, onConfirm, onClose }) {
  const [val, setVal] = useState(initial ?? "");
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ca-prompt-modal"
      role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-navy-950/85" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-navy-900 border border-gold-500/30 w-full max-w-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Mono className="text-gold-400 text-[0.55rem]">{title}</Mono>
          <button onClick={onClose} data-testid="ca-prompt-close" aria-label="Close dialog"><X className="h-4 w-4 text-white/50" /></button>
        </div>
        <Field label={label}>
          <input type="number" autoFocus value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid="ca-prompt-input"
            aria-label={label} onKeyDown={(e) => e.key === "Enter" && val !== "" && onConfirm(+val)} />
        </Field>
        <div className="flex gap-2">
          <button onClick={() => val !== "" && onConfirm(+val)} data-testid="ca-prompt-confirm"
            className="flex-1 bg-gold-500 text-navy-950 font-semibold text-xs py-2.5 hover:bg-gold-400">{confirmText}</button>
          <button onClick={onClose} data-testid="ca-prompt-cancel" className="border border-gold-500/25 text-white/60 text-xs px-4 hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const fmtMoney = (n) => (n == null ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD" }));
const VAR_CLR = { proposed: "text-white/50 border-gold-500/20", directed: "text-sky-300 border-sky-400/40", approved: "text-emerald-400 border-emerald-400/40", rejected: "text-red-400 border-red-500/40" };
const EOT_CLR = { pending_notice: "text-gold-400 border-gold-500/40", notified: "text-sky-300 border-sky-400/40", granted: "text-emerald-400 border-emerald-400/40", refused: "text-red-400 border-red-500/40", time_barred: "text-red-400 border-red-500/40" };

function AddClaim({ contractId, onDone }) {
  const [f, setF] = useState({ period_to: "", amount_claimed: "", reference: "" });
  const add = async () => {
    if (!f.period_to || !f.amount_claimed) return toast.error("Period and amount are required");
    try {
      const { data } = await api.post(`/ca/contracts/${contractId}/claims`, { ...f, amount_claimed: +f.amount_claimed });
      toast.success(`Claim #${data.claim_no} logged — certificate due ${data.cert_due}, SOPA schedule due ${data.sopa_schedule_due}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end border border-gold-500/15 p-4" data-testid="ca-add-claim">
      <Field label="Period to"><input type="date" value={f.period_to} onChange={(e) => setF({ ...f, period_to: e.target.value })} className={inputCls} data-testid="ca-claim-period" /></Field>
      <Field label="Amount claimed ($)"><input type="number" value={f.amount_claimed} onChange={(e) => setF({ ...f, amount_claimed: e.target.value })} className={inputCls} data-testid="ca-claim-amount" /></Field>
      <Field label="Reference"><input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={inputCls} data-testid="ca-claim-ref" /></Field>
      <button onClick={add} data-testid="ca-claim-add-btn" className="bg-gold-500 text-navy-950 font-semibold text-xs py-2.5 hover:bg-gold-400 flex items-center justify-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Log claim</button>
    </div>
  );
}

function ClaimsTab({ d, reload }) {
  const [certifying, setCertifying] = useState(null);
  const certify = async (amt) => {
    if (amt == null || amt === "") return;
    try {
      await api.patch(`/ca/claims/${certifying.id}`, { amount_certified: +amt });
      toast.success("Certificate issued — payment clock started");
      setCertifying(null);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const markPaid = async (cl) => {
    await api.patch(`/ca/claims/${cl.id}`, { paid_at: new Date().toISOString().slice(0, 10) }).catch(() => {});
    toast.success("Marked paid");
    reload();
  };
  return (
    <div className="space-y-4">
      <AddClaim contractId={d.id} onDone={reload} />
      <table className="w-full text-sm" data-testid="ca-claims-table">
        <thead><tr className="text-left border-b border-gold-500/15">
          {["#", "Period", "Claimed", "Cert due", "SOPA schedule due", "Certified", "Payment due", "Status", ""].map((h) => <th key={h} className="px-3 py-2"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>)}
        </tr></thead>
        <tbody>
          {d.claims.map((cl) => (
            <tr key={cl.id} className="border-b border-gold-500/10" data-testid={`ca-claim-row-${cl.id}`}>
              <td className="px-3 py-2.5 text-white/80 text-xs font-mono">{cl.claim_no}</td>
              <td className="px-3 py-2.5 text-white/70 text-xs">{cl.period_to}</td>
              <td className="px-3 py-2.5 text-white/80 text-xs font-mono">{fmtMoney(cl.amount_claimed)}</td>
              <td className="px-3 py-2.5 text-white/70 text-xs">{cl.cert_due}</td>
              <td className="px-3 py-2.5 text-white/70 text-xs">{cl.sopa_schedule_due}</td>
              <td className="px-3 py-2.5 text-white/80 text-xs font-mono">{fmtMoney(cl.amount_certified)}</td>
              <td className="px-3 py-2.5 text-white/70 text-xs">{cl.payment_due || "—"}</td>
              <td className="px-3 py-2.5"><Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${cl.status === "paid" ? "text-emerald-400 border-emerald-400/40" : cl.status === "certified" ? "text-sky-300 border-sky-400/40" : "text-gold-400 border-gold-500/40"}`}>{cl.status}</Mono></td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                {cl.status === "submitted" && <button onClick={() => setCertifying(cl)} data-testid={`ca-certify-${cl.id}`} className="text-gold-400 text-xs hover:text-gold-500">Certify</button>}
                {cl.status === "certified" && <button onClick={() => markPaid(cl)} data-testid={`ca-pay-${cl.id}`} className="text-emerald-400 text-xs hover:text-emerald-300">Mark paid</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.claims.length === 0 && <p className="text-white/40 text-sm text-center py-4" data-testid="ca-claims-empty">No progress claims yet.</p>}
      {certifying && <PromptModal title={`Certify claim #${certifying.claim_no}`} label="Amount to certify ($ ex GST)"
        initial={certifying.amount_claimed} confirmText="Issue certificate" onConfirm={certify} onClose={() => setCertifying(null)} />}
    </div>
  );
}

function VariationsTab({ d, reload }) {
  const [f, setF] = useState({ title: "", amount: "", direction_ref: "", status: "proposed" });
  const add = async () => {
    if (!f.title) return toast.error("Variation title required");
    try {
      await api.post(`/ca/contracts/${d.id}/variations`, { ...f, amount: +f.amount || 0 });
      toast.success("Variation logged");
      setF({ title: "", amount: "", direction_ref: "", status: "proposed" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (v, status) => {
    await api.patch(`/ca/variations/${v.id}`, { status }).catch(() => {});
    reload();
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end border border-gold-500/15 p-4" data-testid="ca-add-variation">
        <div className="md:col-span-2"><Field label="Variation"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="ca-var-title" /></Field></div>
        <Field label="Amount ($)"><input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} data-testid="ca-var-amount" /></Field>
        <Field label="Direction ref"><input value={f.direction_ref} onChange={(e) => setF({ ...f, direction_ref: e.target.value })} className={inputCls} data-testid="ca-var-ref" /></Field>
        <button onClick={add} data-testid="ca-var-add-btn" className="bg-gold-500 text-navy-950 font-semibold text-xs py-2.5 hover:bg-gold-400 flex items-center justify-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Log variation</button>
      </div>
      {d.variations.map((v) => (
        <div key={v.id} className="flex flex-wrap items-center gap-3 border border-gold-500/10 px-4 py-3" data-testid={`ca-var-row-${v.id}`}>
          <Mono className="text-[0.5rem] text-white/40">V{v.vno}</Mono>
          <span className="text-white text-xs flex-1">{v.title} {v.direction_ref && <span className="text-white/40">· {v.direction_ref}</span>}{!v.directed_in_writing && <span className="text-red-400 ml-2 text-[0.65rem]"><AlertTriangle className="h-3 w-3 inline mr-1" />not directed in writing</span>}</span>
          <span className="text-white/80 text-xs font-mono">{fmtMoney(v.amount)}</span>
          <Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${VAR_CLR[v.status]}`}>{v.status}</Mono>
          {v.status !== "approved" && <button onClick={() => setStatus(v, "approved")} data-testid={`ca-var-approve-${v.id}`} className="text-emerald-400 text-xs hover:text-emerald-300">Approve</button>}
        </div>
      ))}
      {d.variations.length === 0 && <p className="text-white/40 text-sm text-center py-4" data-testid="ca-vars-empty">No variations logged.</p>}
    </div>
  );
}

function EotsTab({ d, reload }) {
  const [f, setF] = useState({ cause: "", event_date: "", notice_date: "", days_claimed: "" });
  const [assessing, setAssessing] = useState(null);
  const grant = async (days) => {
    await api.patch(`/ca/eots/${assessing.id}`, { days_granted: days }).catch(() => {});
    setAssessing(null);
    reload();
  };
  const add = async () => {
    if (!f.cause || !f.event_date) return toast.error("Cause and event date required");
    try {
      const { data } = await api.post(`/ca/contracts/${d.id}/eots`, { ...f, days_claimed: +f.days_claimed || 0, notice_date: f.notice_date || null });
      toast[data.time_barred ? "error" : "success"](data.time_barred ? `TIME-BARRED — notice was due ${data.notice_due}` : `EOT logged — notice due ${data.notice_due}`);
      setF({ cause: "", event_date: "", notice_date: "", days_claimed: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end border border-gold-500/15 p-4" data-testid="ca-add-eot">
        <Field label="Cause of delay"><input value={f.cause} onChange={(e) => setF({ ...f, cause: e.target.value })} className={inputCls} data-testid="ca-eot-cause" /></Field>
        <Field label="Delay event date"><input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} className={inputCls} data-testid="ca-eot-event" /></Field>
        <Field label="Notice given (optional)"><input type="date" value={f.notice_date} onChange={(e) => setF({ ...f, notice_date: e.target.value })} className={inputCls} data-testid="ca-eot-notice" /></Field>
        <Field label="Days claimed"><input type="number" value={f.days_claimed} onChange={(e) => setF({ ...f, days_claimed: e.target.value })} className={inputCls} data-testid="ca-eot-days" /></Field>
        <button onClick={add} data-testid="ca-eot-add-btn" className="bg-gold-500 text-navy-950 font-semibold text-xs py-2.5 hover:bg-gold-400 flex items-center justify-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Log EOT</button>
      </div>
      {d.eots.map((e0) => (
        <div key={e0.id} className="flex flex-wrap items-center gap-3 border border-gold-500/10 px-4 py-3" data-testid={`ca-eot-row-${e0.id}`}>
          <span className="text-white text-xs flex-1">{e0.cause}<span className="text-white/40 block text-[0.65rem]">event {e0.event_date} · notice due {e0.notice_due}{e0.notice_date ? ` · given ${e0.notice_date}` : ""}</span></span>
          <span className="text-white/70 text-xs font-mono">{e0.days_claimed}d claimed{e0.days_granted != null ? ` · ${e0.days_granted}d granted` : ""}</span>
          <Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${EOT_CLR[e0.status]}`}>{e0.status.replace("_", " ")}</Mono>
          {["notified", "pending_notice"].includes(e0.status) && <button onClick={() => setAssessing(e0)} data-testid={`ca-eot-grant-${e0.id}`} className="text-gold-400 text-xs hover:text-gold-500">Assess</button>}
        </div>
      ))}
      {d.eots.length === 0 && <p className="text-white/40 text-sm text-center py-4" data-testid="ca-eots-empty">No EOTs logged.</p>}
      {assessing && <PromptModal title="Assess EOT" label={`Days granted (claimed ${assessing.days_claimed})`}
        initial={assessing.days_claimed} confirmText="Record determination" onConfirm={grant} onClose={() => setAssessing(null)} />}
    </div>
  );
}

export default function ContractAdminDetail() {
  const { contractId } = useParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("claims");

  const reload = useCallback(() => {
    api.get(`/ca/contracts/${contractId}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [contractId]);
  useEffect(() => { reload(); }, [reload]);

  const markPC = async () => {
    await api.patch(`/ca/contracts/${contractId}`, { pc_achieved_at: new Date().toISOString().slice(0, 10) }).catch(() => {});
    toast.success("Practical completion recorded — 50% retention released, DLP started");
    reload();
  };

  if (d === null) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>;
  if (d === false) return <div className="p-16 text-center text-white/50">Contract not found. <Link to="/dashboard/contracts" className="text-gold-400">Back to register</Link></div>;

  const s = d.summary;
  const cells = [
    ["Adjusted sum", money(s.adjusted_sum)], ["Certified to date", money(s.certified_total)],
    ["Remaining", money(s.remaining)], ["Retention held", money(s.retention_held)],
    ["EOT days granted", `${s.eot_days_granted}d`], ["PC (revised)", s.revised_pc || d.pc_date || "—"],
  ];

  return (
    <div className="space-y-6" data-testid="ca-detail-page">
      <div>
        <Link to="/dashboard/contracts" className="text-white/40 text-xs hover:text-gold-400 inline-flex items-center gap-1" data-testid="ca-back"><ArrowLeft className="h-3 w-3" /> Contracts register</Link>
        <div className="flex flex-wrap items-start justify-between gap-4 mt-1">
          <div>
            <h1 className="font-heading text-2xl text-white flex items-center gap-3"><FileSignature className="h-5 w-5 text-gold-400" /> {d.title}</h1>
            <p className="text-white/50 text-xs mt-1">{d.contractor} · {d.form} · {(d.state || "").toUpperCase()} — {d.rules?.sopa?.act}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${API}/ca/contracts/${contractId}/export/authority?token=${localStorage.getItem("mdau_token")}`} data-testid="ca-authority-csv"
              className="border border-sky-400/40 text-sky-300 text-xs px-4 py-2 hover:bg-sky-400/10 flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" /> Authority job-cost CSV
            </a>
            {s.phase === "construction" && (
              <button onClick={markPC} data-testid="ca-mark-pc" className="border border-emerald-400/40 text-emerald-400 text-xs px-4 py-2 hover:bg-emerald-400/10 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Record practical completion</button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-gold-500/20" data-testid="ca-summary-cells">
        {cells.map(([l, v]) => (
          <div key={l} className="border-r border-b border-gold-500/20 bg-navy-900/40 p-4">
            <Mono className="text-[0.45rem] text-white/40 block">{l}</Mono>
            <div className="font-mono text-sm text-white mt-1.5">{v}</div>
          </div>
        ))}
      </div>

      {d.deadlines.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="ca-detail-deadlines">
          {d.deadlines.map((dl, i) => (
            <span key={i} className={`border px-2.5 py-1.5 text-[0.65rem] ${DL_CLR[dl.status]}`}>{dl.status === "overdue" && <AlertTriangle className="h-3 w-3 inline mr-1" />}{dl.title} · {dl.due}</span>
          ))}
        </div>
      )}

      <div className="inline-flex border border-gold-500/25" data-testid="ca-tabs">
        {[["claims", `Progress claims (${d.claims.length})`], ["variations", `Variations (${d.variations.length})`], ["eots", `EOTs (${d.eots.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ca-tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest ${tab === k ? "bg-gold-500 text-navy-950 font-semibold" : "text-white/55 hover:text-gold-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "claims" && <ClaimsTab d={d} reload={reload} />}
      {tab === "variations" && <VariationsTab d={d} reload={reload} />}
      {tab === "eots" && <EotsTab d={d} reload={reload} />}
    </div>
  );
}
